import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface FeedbackButtonsProps {
  contentItemId: string | null;
  onFeedbackSubmitted?: () => void;
}

export const FeedbackButtons = ({ contentItemId, onFeedbackSubmitted }: FeedbackButtonsProps) => {
  const { user } = useAuth();
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState<'like' | 'dislike' | null>(null);

  const submitFeedback = async (feedbackType: 'like' | 'dislike', reasonText?: string) => {
    if (!user || !contentItemId) {
      toast.error("Please log in to provide feedback");
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('feedback')
        .upsert({
          user_id: user.id,
          content_item_id: contentItemId,
          feedback_type: feedbackType,
          reason: reasonText || null
        }, {
          onConflict: 'user_id,content_item_id'
        });

      if (error) throw error;

      setCurrentFeedback(feedbackType);
      toast.success(`Thank you for your ${feedbackType === 'like' ? 'positive' : 'negative'} feedback!`);
      onFeedbackSubmitted?.();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error("Failed to submit feedback");
    } finally {
      setIsSubmitting(false);
      setShowReasonDialog(false);
      setReason("");
    }
  };

  const handleLike = () => {
    submitFeedback('like');
  };

  const handleDislike = () => {
    setShowReasonDialog(true);
  };

  const handleReasonSubmit = () => {
    if (reason.trim()) {
      submitFeedback('dislike', reason.trim());
    } else {
      submitFeedback('dislike');
    }
  };

  if (!user || !contentItemId) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-2 p-4 bg-muted/10 rounded-lg border border-border">
        <span className="text-sm font-medium text-muted-foreground">How was the humanized text?</span>
        <div className="flex gap-2 ml-auto">
          <Button
            variant={currentFeedback === 'like' ? 'default' : 'outline'}
            size="sm"
            onClick={handleLike}
            disabled={isSubmitting}
            className="flex items-center gap-1"
          >
            <ThumbsUp className="w-4 h-4" />
            Good
          </Button>
          <Button
            variant={currentFeedback === 'dislike' ? 'destructive' : 'outline'}
            size="sm"
            onClick={handleDislike}
            disabled={isSubmitting}
            className="flex items-center gap-1"
          >
            <ThumbsDown className="w-4 h-4" />
            Not Good
          </Button>
        </div>
      </div>

      <Dialog open={showReasonDialog} onOpenChange={setShowReasonDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Help us improve</DialogTitle>
            <DialogDescription>
              Please tell us what you didn't like about the humanized text so we can improve it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">What didn't you like about it?</Label>
              <Textarea
                id="reason"
                placeholder="Please explain what went wrong or what could be better..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[100px]"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowReasonDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleReasonSubmit} disabled={isSubmitting}>
                Submit Feedback
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};